import {computed} from 'vue';
import {defineStore} from 'pinia';
import {ref, useHasEnoughPriviledge} from '#imports';
import {readonlyState} from '@malt/utils';
import type {OtherProfessionalBackground, ProfessionalBackground, ProfileQualificationResource, RatedSkill} from '@api';
import {Configuration, ProfileQualificationApi, ProfileQualificationProfessionalBackgroundApi} from '@api';

interface QualificationUpdateForm {
    comatchLegacyNotes: string;
    commentOnAvailability: string;
    commentOnDailyRate: string;
    commentOnFunctionalExpertise: string;
    commentOnIndustryExpertise: string;
    commentOnMobility: string;
    commentOnOtherExpertise: string;
    engagementComment: string;
    functionalSkillRatings: RatedSkill[];
    industrySkillRatings: RatedSkill[];
    interestInInterim: boolean;
    internalSeniorityLevel: number | null;
    limitations: string;
    noLongerAvailableDueToPermanentEmployment: boolean;
    otherSkillRatings: RatedSkill[];
    professionalInterests: string;
    selectedOtherProfessionalBackgrounds: OtherProfessionalBackground[];
    selectedProfessionalBackgrounds: ProfessionalBackground[];
    softSkills: string;
}

const apiConfiguration = new Configuration({basePath: ''});
const profileQualificationApi = new ProfileQualificationApi(apiConfiguration);
const profileQualificationProfessionalBackgroundApi = new ProfileQualificationProfessionalBackgroundApi(apiConfiguration);

export const useProfileQualificationStore = defineStore('profileQualification', () => {
    const initialized = computed(() => !!freelancerProfileId.value);
    const isAllowedToEdit = ref(false);
    const inEditMode = ref(false);
    const sourcingProjectId = ref<string | undefined>();
    const freelancerProfileId = ref('');
    const qualification = ref<ProfileQualificationResource>({
        profileId: '',
        commentOnAvailability: {
            value: '',
        },
        commentOnDailyRate: {
            value: '',
        },
        commentOnFunctionalExpertise: {
            value: '',
        },
        commentOnIndustryExpertise: {
            value: '',
        },
        commentOnMobility: {
            value: '',
        },
        commentOnOtherExpertise: {
            value: '',
        },
        engagementComment: '',
        functionalSkillRatings: [],
        industrySkillRatings: [],
        interestInInterim: false,
        internalSeniorityLevel: 1,
        limitations: {
            value: '',
        },
        language: '',
        noLongerAvailableDueToPermanentEmployment: false,
        otherSkillRatings: [],
        professionalInterests: {
            value: '',
        },
        selectedOtherProfessionalBackgrounds: [],
        selectedProfessionalBackgrounds: [],
        softSkills: {
            value: '',
        },
        availabilityStatus: 'AVAILABLE',
        sourcingStatus: {
            eligible: true,
        },
    });
    const allProfessionalBackgrounds = ref<ProfessionalBackground[]>([]);

    const qualificationUpdateForm = ref(initQualificationUpdateForm(qualification.value));

    async function init(profileId: string, projectId?: string) {
        if (initialized.value && freelancerProfileId.value === profileId) {
            return;
        }
        freelancerProfileId.value = profileId;
        sourcingProjectId.value = projectId;
        isAllowedToEdit.value = await useHasEnoughPriviledge(['ROLE_PROFILE_QUALIFICATION_EDITION']);
        qualification.value = await profileQualificationApi.getProfileQualification({profileId});
        allProfessionalBackgrounds.value = await profileQualificationProfessionalBackgroundApi.getAll();
        qualificationUpdateForm.value = initQualificationUpdateForm(qualification.value);

        return {
            qualification: qualification.value,
        };
    }

    function initQualificationUpdateForm(qualification: ProfileQualificationResource): QualificationUpdateForm {
        return {
            comatchLegacyNotes: qualification.comatchLegacyNotes || '',
            commentOnAvailability: qualification.commentOnAvailability.value,
            commentOnDailyRate: qualification.commentOnDailyRate.value,
            commentOnFunctionalExpertise: qualification.commentOnFunctionalExpertise.value,
            commentOnIndustryExpertise: qualification.commentOnIndustryExpertise.value,
            commentOnMobility: qualification.commentOnMobility.value,
            commentOnOtherExpertise: qualification.commentOnOtherExpertise.value,
            engagementComment: qualification.engagementComment,
            functionalSkillRatings: qualification.functionalSkillRatings,
            industrySkillRatings: qualification.industrySkillRatings,
            interestInInterim: qualification.interestInInterim,
            internalSeniorityLevel: qualification.internalSeniorityLevel ?? null,
            limitations: qualification.limitations.value,
            noLongerAvailableDueToPermanentEmployment: qualification.noLongerAvailableDueToPermanentEmployment,
            otherSkillRatings: qualification.otherSkillRatings,
            professionalInterests: qualification.professionalInterests.value,
            selectedOtherProfessionalBackgrounds: qualification.selectedOtherProfessionalBackgrounds,
            selectedProfessionalBackgrounds: qualification.selectedProfessionalBackgrounds,
            softSkills: qualification.softSkills.value,
        };
    }

    function checkIsAllowedToEdit() {
        if (!isAllowedToEdit.value) {
            throw new Error("You're trying to use an action that requires edition privileges");
        }
    }

    function checkInitialized() {
        if (!initialized.value) {
            throw new Error('The store is not initialized yet');
        }
    }

    function enterEditMode() {
        checkIsAllowedToEdit();
        resetQualificationUpdateForm();
        inEditMode.value = true;
    }

    function leaveEditMode() {
        resetQualificationUpdateForm();
        inEditMode.value = false;
    }

    function resetQualificationUpdateForm() {
        checkInitialized();
        qualificationUpdateForm.value = initQualificationUpdateForm(qualification.value);
    }

    async function saveProfileQualification() {
        checkInitialized();
        checkIsAllowedToEdit();
        qualification.value = await profileQualificationApi.upsertProfileQualification({
            profileId: freelancerProfileId.value,
            profileQualificationRequest: {
                ...qualificationUpdateForm.value,
                internalSeniorityLevel: qualificationUpdateForm.value.internalSeniorityLevel ?? undefined,
                selectedProfessionalBackgrounds: qualificationUpdateForm.value.selectedProfessionalBackgrounds.map((it) => it.id),
                relatedSourcingProjectId: sourcingProjectId.value,
            },
        });
        leaveEditMode();
    }

    const formHasChanged = computed<boolean>(
        () => JSON.stringify(initQualificationUpdateForm(qualification.value)) !== JSON.stringify(qualificationUpdateForm.value),
    );

    return {
        init,
        isAllowedToEdit: readonlyState(isAllowedToEdit),
        inEditMode: readonlyState(inEditMode),
        qualification,
        allProfessionalBackgrounds: readonlyState(allProfessionalBackgrounds),
        qualificationUpdateForm,
        enterEditMode,
        leaveEditMode,
        saveProfileQualification,
        formHasChanged,
    };
});
